import OutboundInternalRequestsApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import BnkWarehouseLocationApiV2 from 'api/v2/admins/bnk-warehouse-location-api-v2'
import TitlePage from 'components/atoms/title-page'
import InternalRequestDetail from 'domains/internal-request/organisms/internal-request-detail'
import PropTypes from 'prop-types'
import { useParams } from 'react-router'
import React, { useEffect, useRef, useState } from 'react'
import { STATUS } from 'domains/internal-request/constants/internal-request-item-constants'
import { ALERT_TYPES } from 'constants/alert-constants'
import { Button, Card, CardBody, CardHeader, CardTitle, Input, Table } from 'reactstrap'
import InternalRequestItemTable from 'domains/internal-request/organisms/item-table'
import { ITEM_STATUS } from 'domains/internal-request/constants'
import withLoader from 'hocs/with-loader'
import CardLoader from 'components/organisms/card-loader'
import TablePagination from 'components/molecules/table-pagination'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const InternalRequestDetailInboundOpsPage = ({ pageUtils }) => {
  const [ internalRequest, setInternalRequest ]               = useState({})
  const [ internalRequestLoading, setInternalRequestLoading ] = useState(true)
  const [ items, setItems ]                                   = useState({
    pending   : [],
    completed : []
  })
  const [ itemsLoading, setItemsLoading ]                     = useState({
    pending   : false,
    completed : false
  })
  const [ itemsPagination, setItemsPagination ]               = useState({
    pending   : {},
    completed : {}
  })
  const [ warehouseLocations, setWarehouseLocations ]         = useState([])
  const [ selectedReceiveItems, setSelectedReceiveItems ]     = useState({})
  const [ receiveFilters, setReceiveFilters ]                 = useState({
    sku  : '',
    name : ''
  })
  const [ pendingTableState, setPendingTableState ]           = useState({
    filters   : [],
    pageIndex : 0,
    pageSize  : 10
  })
  const [ receiving, setReceiving ] = useState(false)
  const receiveRunning = useRef(false)
  const { id }                                                = useParams()

  useEffect(() => {
    // show internal request endpoint
    OutboundInternalRequestsApiV2.show(id)
      .then((response) => {
        setInternalRequest(response.data.internal_request)
        setInternalRequestLoading(false)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }, [ pageUtils, id ])

  useEffect(() => {
    BnkWarehouseLocationApiV2.get('', { limit: 200 })
      .then((response) => {
        setWarehouseLocations(response.data?.bnk_warehouse_locations || [])
      })
      .catch(() => setWarehouseLocations([]))
  }, [])

  /** all methods for fetching internal request items */
  const handleFetchPendingItems = async (tableState = pendingTableState) => {
    setItemsLoading((prevState) => ({ ...prevState, pending: true }))
    const additionalFilters = {
      status        : [ ITEM_STATUS.BORROWED, ITEM_STATUS.MARKED_FOR_RETURN, ITEM_STATUS.PARTIALLY_RETURNED, ITEM_STATUS.RETURNED ],
      inventory_sku : receiveFilters.sku,
      name          : receiveFilters.name
    }
    await OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      additionalFilters   : additionalFilters,
      tableState          : tableState
    })
      .then((response) => {
        setItems((prevState) => ({ ...prevState, pending: response.data.internal_request_items }))
        setItemsPagination((prevState) => ({ ...prevState, pending: response.data.meta }))
      })
      .catch(() => pageUtils.setAlertMsg(
        'fetching pending items error'
        , ALERT_TYPES.DANGER
      ))
      .finally(() => setItemsLoading((prevState) => ({ ...prevState, pending: false })) )
  }

  const handleFetchCompletedItems = async (tableState) => {
    setItemsLoading((prevState) => ({ ...prevState, completed: true }))
    const additionalFilters = {
      status: [ STATUS.COMPLETED ]
    }
    await OutboundInternalRequestsApiV2.getItems({
      internal_request_id : id,
      additionalFilters   : additionalFilters,
      tableState          : tableState
    })
      .then((response) => {
        setItems((prevState) => ({ ...prevState, completed: response.data.internal_request_items }))
        setItemsPagination((prevState) => ({ ...prevState, completed: response.data.meta }))
      })
      .catch(() => pageUtils.setAlertMsg(
        'fetching completed items error'
        , ALERT_TYPES.DANGER
      ))
      .finally(() => setItemsLoading((prevState) => ({ ...prevState, completed: false })) )
  }
  /** end of methods for fetching internal request items */

  /** methods for processing items */
  const locationOptionsForItem = (item) => (item.inventory?.inventory_locations || item.inventory?.locations || []).filter((location) => (location.type_name || location.location) === 'Lokasi Item' && location.active !== false)

  const locationOptionId = (location) => location?.product_location_id ? `product:${location.product_location_id}` : location?.bnk_warehouse_location_id ? `warehouse:${location.bnk_warehouse_location_id}` : location?.location_id ? `legacy:${location.location_id}` : location?.id ? `product:${location.id}` : ''

  const locationOptionLabel = (location) => location?.label || `${location?.location || '-'} [${location?.sublocation || location?.sub_location || location?.name || '-'}]`

  const uniqueLocationOptions = (locations) => {
    const seen = {}

    return locations.filter((location) => {
      const key = locationOptionId(location) || locationOptionLabel(location)
      if (!key || seen[key]) return false

      seen[key] = true
      return true
    })
  }

  const warehouseLocationOptionsForItem = (item) => {
    const existingLabels = locationOptionsForItem(item).map((location) => locationOptionLabel(location))

    return warehouseLocations.filter((location) => location.active !== false && (location.type_name || location.location) === 'Lokasi Item' && !existingLabels.includes(locationOptionLabel(location)))
  }

  const defaultLocationIdForItem = () => ''

  const handleToggleReceiveItem = (item) => {
    setSelectedReceiveItems((prevState) => {
      const nextState = { ...prevState }

      if (nextState[item.id]) {
        delete nextState[item.id]
      }
      else {
        nextState[item.id] = {
          id          : item.id,
          quantity    : item.quantity,
          location_id : defaultLocationIdForItem(item)
        }
      }

      return nextState
    })
  }

  const handleToggleAllReceiveItems = () => {
    setSelectedReceiveItems((prevState) => {
      const allDisplayedSelected = displayedPendingItems.length > 0 && displayedPendingItems.every((item) => prevState[item.id])
      const nextState = { ...prevState }

      if (allDisplayedSelected) {
        displayedPendingItems.forEach((item) => {
          delete nextState[item.id]
        })

        return nextState
      }

      displayedPendingItems.forEach((item) => {
        nextState[item.id] = {
          id          : item.id,
          quantity    : prevState[item.id]?.quantity || item.quantity,
          location_id : prevState[item.id]?.location_id || defaultLocationIdForItem(item)
        }
      })

      return nextState
    })
  }

  const handleReceiveQuantityChange = (item, quantity) => {
    setSelectedReceiveItems((prevState) => ({
      ...prevState,
      [item.id]: {
        id          : item.id,
        quantity    : quantity,
        location_id : prevState[item.id]?.location_id || defaultLocationIdForItem(item)
      }
    }))
  }

  const handleReceiveLocationChange = (item, locationId) => {
    setSelectedReceiveItems((prevState) => ({
      ...prevState,
      [item.id]: {
        id          : item.id,
        quantity    : prevState[item.id]?.quantity || item.quantity,
        location_id : locationId
      }
    }))
  }

  const selectedReceivePayload = () => Object.values(selectedReceiveItems).map((selectedItem) => {
    const [ locationType, locationId ] = String(selectedItem.location_id || '').split(':')

    return {
      id       : selectedItem.id,
      quantity : selectedItem.quantity,
      ...(locationType === 'product' ? { product_location_id: locationId } : locationType === 'warehouse' ? { bnk_warehouse_location_id: locationId } : { tl_warehouse_location_id: locationId })
    }
  })

  const handleReturnAndPutAwayItems = () => {
    if (receiveRunning.current) return
    const selectedItems = selectedReceivePayload()

    if (!selectedItems.length) {
      pageUtils.setAlertMsg('Pilih minimal satu item untuk receive.', ALERT_TYPES.WARNING)
      return
    }

    if (selectedItems.some((item) => !item.product_location_id && !item.bnk_warehouse_location_id && !item.tl_warehouse_location_id)) {
      pageUtils.setAlertMsg('Pilih lokasi put away aktual untuk setiap item yang dipilih, termasuk item di halaman lain.', ALERT_TYPES.WARNING)
      return
    }
    receiveRunning.current = true
    setReceiving(true)
    OutboundInternalRequestsApiV2.returnAndPutAway({
      internal_request_id : internalRequest.id,
      items               : selectedItems
    })
      .then(() => {
        pageUtils.setAlertMsg('Internal Request items berhasil di-receive dan put away.', ALERT_TYPES.SUCCESS)
        setSelectedReceiveItems({})
        handleFetchPendingItems()
        handleFetchCompletedItems()
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: 'Receive belum terkonfirmasi. Refresh dan periksa Completed Items sebelum mencoba lagi.' } }))
      .finally(() => { receiveRunning.current = false; setReceiving(false) })
  }
  /** end of methods for processing items */

  useEffect(() => {
    handleFetchPendingItems(pendingTableState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id, pendingTableState.pageIndex, pendingTableState.pageSize, receiveFilters.sku, receiveFilters.name ])

  useEffect(() => {
    handleFetchCompletedItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id ])

  const handleReceiveFilterChange = (key, value) => {
    setReceiveFilters((prevState) => ({ ...prevState, [key]: value }))
    setPendingTableState((prevState) => ({ ...prevState, pageIndex: 0 }))
  }

  const displayedPendingItems = items.pending.filter((item) => {
    const sku = item.inventory?.inventory_sku?.toString().toLowerCase() || ''
    const name = item.inventory?.product?.name?.toString().toLowerCase() || ''

    return sku.includes(receiveFilters.sku.toLowerCase()) && name.includes(receiveFilters.name.toLowerCase())
  })

  const pendingPageCount = itemsPagination.pending?.total_pages || 1
  const pendingCanPreviousPage = pendingTableState.pageIndex > 0
  const pendingCanNextPage = pendingTableState.pageIndex < pendingPageCount - 1
  const selectedReceiveItemsCount = Object.keys(selectedReceiveItems).length

  const gotoPendingPage = (pageIndex) => {
    const lastPageIndex = Math.max(pendingPageCount - 1, 0)
    const nextPageIndex = Math.min(Math.max(pageIndex, 0), lastPageIndex)

    setPendingTableState((prevState) => ({ ...prevState, pageIndex: nextPageIndex }))
  }

  const setPendingPageSize = (pageSize) => {
    setPendingTableState((prevState) => ({
      ...prevState,
      pageIndex : 0,
      pageSize  : Number(pageSize)
    }))
  }

  const isAllDisplayedPendingSelected = displayedPendingItems.length > 0 && displayedPendingItems.every((item) => selectedReceiveItems[item.id])

  const InternalRequestDetailCardWithLoader = withLoader(InternalRequestDetail, CardLoader)

  /** items table hidden columns */
  const putAwayItemsHiddenColumns = [
    'status',
    'reject_reason',
    'reject_note',
    'marked_for_return',
    'quantity',
    'quantity_picked',
    'quantity_requested',
    'quantity_to_borrow',
    'quantity_in_warehouse',
    'borrowed',
    'returned',
    'unreturned'
  ]

  return (
    <>
      <TitlePage mainTitle={ 'Internal Request Inbound Receive' } subTitle={ internalRequest.code } />

      <InternalRequestDetailCardWithLoader
        isLoading={ internalRequestLoading }
        internal_request={ internalRequest }
      />

      { /* Pending Items Table */ }
      <Card>
        <CardHeader>
          <CardTitle className="text-default font-weight-bolder">Items To Receive</CardTitle>
        </CardHeader>
        <CardBody>
          <div className='mb-2 text-muted small'>Pilih lokasi aktual setelah barang kembali (contoh: SCBD - Transit_Display). Klik Receive Items untuk menyimpan lokasi dan menyelesaikan put away; SQ/IQ tetap. Pilihan lokasi dispatch Event tidak otomatis menjadi lokasi pengembalian.</div>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div className='text-muted small'>
              { selectedReceiveItemsCount > 0 ? `${selectedReceiveItemsCount} selected item(s) across pages` : 'No items selected' }
            </div>
            <Button
              color='success'
              disabled={ receiving }
              onClick={ handleReturnAndPutAwayItems }
            >
              { receiving ? 'Receiving...' : 'Receive Items' }
            </Button>
          </div>

          <fieldset disabled={ receiving }>
          <Table responsive hover bordered size='sm'>
            <thead>
              <tr>
                <th>
                  <Input
                    type='checkbox'
                    checked={ isAllDisplayedPendingSelected }
                    onChange={ handleToggleAllReceiveItems }
                  />
                </th>
                <th>Photo</th>
                <th>Code</th>
                <th>SKU</th>
                <th>Name</th>
                <th>Borrowed Qty</th>
                <th>Receive Qty</th>
                <th>Put Away Location</th>
                <th>Status</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                <th></th>
                <th>
                  <Input
                    bsSize='sm'
                    placeholder='Search SKU...'
                    value={ receiveFilters.sku }
                    onChange={ (event) => handleReceiveFilterChange('sku', event.target.value) }
                  />
                </th>
                <th>
                  <Input
                    bsSize='sm'
                    placeholder='Search name...'
                    value={ receiveFilters.name }
                    onChange={ (event) => handleReceiveFilterChange('name', event.target.value) }
                  />
                </th>
                <th></th>
                <th></th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              { displayedPendingItems.map((item) => {
                const locationOptions = uniqueLocationOptions(locationOptionsForItem(item))
                const warehouseLocationOptions = uniqueLocationOptions(warehouseLocationOptionsForItem(item))
                const selectedLocationId = selectedReceiveItems[item.id]?.location_id || defaultLocationIdForItem(item)

                return (
                  <tr key={ item.id }>
                    <td>
                      <Input
                        type='checkbox'
                        checked={ !!selectedReceiveItems[item.id] }
                        onChange={ () => handleToggleReceiveItem(item) }
                      />
                    </td>
                    <td>
                      <img style={{ width: '6rem' }} src={ item.inventory?.product?.main_image } alt={ item.inventory?.product?.name } />
                    </td>
                    <td>{ item.internal_request_code || internalRequest.code }</td>
                    <td>{ item.inventory?.inventory_sku }</td>
                    <td>{ item.inventory?.product?.name }</td>
                    <td>{ item.quantity }</td>
                    <td>
                      <Input
                        type='number'
                        min={ 1 }
                        max={ item.quantity }
                        value={ selectedReceiveItems[item.id]?.quantity || item.quantity }
                        onChange={ (event) => handleReceiveQuantityChange(item, event.target.value) }
                      />
                    </td>
                    <td style={{ minWidth: 220 }}>
                      <Input
                        type='select'
                        value={ selectedLocationId }
                        onChange={ (event) => handleReceiveLocationChange(item, event.target.value) }
                      >
                        <option value=''>-- Pilih lokasi put away aktual --</option>
                        { locationOptions.map((location) => (
                          <option key={ locationOptionId(location) } value={ locationOptionId(location) }>
                            { locationOptionLabel(location) }
                          </option>
                        )) }
                        { warehouseLocationOptions.length > 0 && (
                          <option disabled value='warehouse-location-section'>-- Available warehouse locations --</option>
                        ) }
                        { warehouseLocationOptions.map((location) => (
                          <option key={ `warehouse-${locationOptionId(location)}` } value={ locationOptionId(location) }>
                            { locationOptionLabel(location) }
                          </option>
                        )) }
                      </Input>
                    </td>
                    <td>{ item.status }</td>
                  </tr>
                )
              }) }
              { displayedPendingItems.length === 0 && (
                <tr>
                  <td colSpan='9' className='text-center text-muted'>No items to receive.</td>
                </tr>
              ) }
            </tbody>
          </Table>
          </fieldset>

          {
            pendingPageCount > 1 && (
              <TablePagination
                canPreviousPage={ pendingCanPreviousPage }
                canNextPage={ pendingCanNextPage }
                pageCount={ pendingPageCount }
                pageIndex={ pendingTableState.pageIndex }
                pageSize={ pendingTableState.pageSize }
                gotoPage={ gotoPendingPage }
                setPageSize={ setPendingPageSize }
                nextPage={ () => gotoPendingPage(pendingTableState.pageIndex + 1) }
                previousPage={ () => gotoPendingPage(pendingTableState.pageIndex - 1) }
              />
            )
          }
        </CardBody>
      </Card>

      { /* Completed Items Table */ }
      <Card>
        <CardHeader>
          <CardTitle className="text-default font-weight-bolder">Completed Items</CardTitle>
        </CardHeader>
        <CardBody>
          <InternalRequestItemTable
            data={ items.completed }
            pagination={ itemsPagination.completed }
            isLoading={ itemsLoading.completed }
            onFetchData={ handleFetchCompletedItems }
            hiddenColumns={ putAwayItemsHiddenColumns }
          />
        </CardBody>
      </Card>
    </>
  )
}

InternalRequestDetailInboundOpsPage.propTypes = propTypes

export default InternalRequestDetailInboundOpsPage
